import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import {
  BuyerFormValues,
  ConsigneeFormValues,
  ConsignorFormValues, SellerFormValues,
  TraderType,
} from 'features/declaration/models/schemas/tradersSchema'
import {
  isBuyer, isConsignee, isConsignor, isSeller, nonNullBuyer, nonNullConsignee, nonNullConsignorIgnoringContactPerson, nonNullSeller,
} from 'features/declaration/models/helpers'
import useSaveConsignmentDetail from '../../consignment-detail/components/useSaveConsignmentDetail'
import { ErrorResult, SaveResult } from '../../../../shared/models/types'
import { ConsignmentDetailDetails } from '../../consignment-detail/models/types'

type SaveConsignmentDetailsFn = (
  transitOperationId: number,
  isDraft: boolean,
  trader: TraderType
) => Promise<SaveResult<ConsignmentDetailDetails>>

type SaveTradersResult = {
  consignee: ConsigneeFormValues | null
  consignor: ConsignorFormValues | null
  seller: SellerFormValues | null
  buyer: BuyerFormValues | null
  errors: ErrorResult[]
}

async function saveHouseConsignmentTraders(
  transitOperationId: number,
  isDraft: boolean,
  formValues: HouseConsignmentFormValues,
  saveFn: SaveConsignmentDetailsFn,
) {
  const result: SaveTradersResult = {
    consignee: null,
    consignor: null,
    seller: null,
    buyer: null,
    errors: [],
  }

  if (formValues.consignee != null && nonNullConsignee(formValues.consignee)) {
    const consigneeSaveResult = await saveFn(transitOperationId, isDraft, formValues.consignee)
    if (consigneeSaveResult.data && isConsignee(consigneeSaveResult.data)) {
      result.consignee = consigneeSaveResult.data
    } else {
      result.errors.push(...consigneeSaveResult.errors)
    }
  }

  if (formValues.consignor != null && nonNullConsignorIgnoringContactPerson(formValues.consignor)) {
    const consignorSaveResult = await saveFn(transitOperationId, isDraft, formValues.consignor)
    if (consignorSaveResult.data && isConsignor(consignorSaveResult.data)) {
      result.consignor = consignorSaveResult.data
    } else {
      result.errors.push(...consignorSaveResult.errors)
    }
  }

  if (formValues.buyer != null && nonNullBuyer(formValues.buyer)) {
    const buyerSaveResult = await saveFn(transitOperationId, isDraft, formValues.buyer)

    if (buyerSaveResult.data && isBuyer(buyerSaveResult.data)) {
      result.buyer = buyerSaveResult.data
    } else {
      result.errors.push(...buyerSaveResult.errors)
    }
  }

  if (formValues.seller != null && nonNullSeller(formValues.seller)) {
    const sellerSaveResult = await saveFn(transitOperationId, isDraft, formValues.seller)

    if (sellerSaveResult.data && isSeller(sellerSaveResult.data)) {
      result.seller = sellerSaveResult.data
    } else {
      result.errors.push(...sellerSaveResult.errors)
    }
  }

  return result
}

function useSaveHouseConsignmentTradersFormValues(transitOperationId: number) {
  const { saveConsignmentDetail } = useSaveConsignmentDetail()

  const saveTraders = async (
    isDraft: boolean,
    formValues: HouseConsignmentFormValues,
  ) => await saveHouseConsignmentTraders(transitOperationId, isDraft, formValues, saveConsignmentDetail)

  return {
    saveTraders,
  }
}

export default useSaveHouseConsignmentTradersFormValues
